<template>
  <div class="dashboard-edit" :style="cssVars">
    <dashboard-layout
      v-model="configs"
      :items="configs"
      :loading="serversLoading"
      :isEdit="true"
      :draggable="true"
      :resizable="true"
      @updateFilter="updateFilter"
      @edit="editWidgetLayout"
      @remove="removeWidgetLayout"
      ref="dashboardlayout"
    >
      <template #name>
        <edit-button :editOptions="['Save']" @click="save()">
          <template>
            <span class="text">{{ $t('button.save') }}</span>
          </template>
        </edit-button>
      </template>
      <template #actions>
        <b-dropdown-item @click="isModalAdd = true" aria-role="listitem">{{
          $t('dashboards.add_element')
        }}</b-dropdown-item>
        <b-dropdown-item @click="isBackgroundImageModalActive = true" aria-role="listitem">{{
          $t('dashboards.set_background')
        }}</b-dropdown-item>
        <b-dropdown-item v-if="configs.id" @click="openDialogRemove" aria-role="listitem">{{
          $t('dashboards.delete')
        }}</b-dropdown-item>
      </template>
    </dashboard-layout>
    <modal-layout
      :active="isModalAdd"
      :items="uiElements"
      :title="$t('dashboards.add_dashboard_element')"
      :libraryName="$t('dashboards.configuredElements')"
      :buttonName="$t('dashboards.add_to_dashboard')"
      :isLoading="modalLoading"
      field="name"
      field_description="default_description"
      @click:load="addElement($event)"
      @close="isModalAdd = false"
    ></modal-layout>
    <b-modal
      :active.sync="isBackgroundImageModalActive"
      has-modal-card
      trap-focus
      @close="isBackgroundImageModalActive = false"
      aria-role="dialog"
      aria-modal
      class="modal background"
    >
      <div class="modal-card">
        <section class="modal-card-body">
          <h1 class="field is-horizontal">{{ $t('dashboards.set_background') }}</h1>
          <b-field horizontal>
            <template #label>
              <b-tooltip :label="$t('dashboards.background_color')" type="is-dark" multilined>
                {{ $t('dashboards.background_color') }}
              </b-tooltip>
            </template>
            <input v-model="tempBackgroundColor" type="color" id="head" name="head" class="color-selector" />
          </b-field>

          <b-field horizontal>
            <template #label>
              <b-tooltip :label="$t('dashboards.background_image')" type="is-dark" multilined>
                {{ $t('dashboards.background_image') }}
              </b-tooltip>
            </template>
            <div class="rows">
              <div class="row">
                <a :href="tempBackgroundImage">{{ configs.background_image }}</a>
              </div>
              <div class="row">
                <b-upload @input="handleUpload" accept="image/*" drag-drop>
                  {{ $t('reports.upload.title') }}
                  <b-loading :active.sync="backgroundImageLoading" :is-full-page="false"></b-loading>
                </b-upload>
              </div>
            </div>
          </b-field>

          <div class="field is-horizontal">
            <div>
              <edit-button v-if="!serversLoading" :editOptions="['remove']" :danger="true" @click="removeBackground">
                <template>
                  <span class="text">{{ this.$t('button.remove') }}</span>
                </template>
              </edit-button>
              <loader v-else></loader>
            </div>
            <div class="spacer"></div>
            <div>
              <edit-button v-if="!serversLoading" :editOptions="['set']" @click="setBackground">
                <template>
                  <span class="text">{{ this.$t('button.set') }}</span>
                </template>
              </edit-button>
              <loader v-else></loader>
            </div>
          </div>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex'
import DashboardLayout from '@/layout/DashboardLayout'
import EditButton from '@/components/EditButton'
import ModalLayout from '@/layout/ModalLayout'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Dashboard',
  components: {
    DashboardLayout,
    EditButton,
    ModalLayout
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role,
      title: state => state.title
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    },
    cssVars() {
      return {
        '--page-background-color': this.configs ? this.configs.background_color : '#ffffff',
        '--page-background-image': this.configs ? `url(${this.configs.background_image})` : 'none'
      }
    }
  },
  data() {
    return {
      isModalAdd: false,
      isDashboardEditAllowed: false,
      serversLoading: false,
      loadingExport: false,
      configs: { name: 'My Dashboard', elements: [] },
      status: '',
      uiElements: [],
      modalLoading: false,
      isBackgroundImageModalActive: false,
      backgroundImageLoading: false,
      tempBackgroundImage: '',
      tempBackgroundColor: '#fff',
      mousePosition: null,
      resizeElementKey: null,
      moveElementKey: null
    }
  },
  mounted() {
    if (this.$route.params.dashboard_id !== undefined && this.$route.params.dashboard_id !== 'add_dashboard') {
      this.getDefaultConfig()
    } else {
      this.setTitle(this.configs.name)
      this.setDashboardName(this.configs.name)
      this.getUiElements()
    }
  },
  methods: {
    ...mapActions('general', ['setTitle', 'setDashboardName']),
    editWidgetLayout(element_id) {
      this.openDialogSave(element_id)
    },
    removeWidgetLayout(element_id) {
      this.configs.elements = this.configs.elements.filter(item => {
        if (item.id == element_id) {
          return false
        }
        return true
      })
      this.$forceUpdate()
    },
    openDialogSave(element_id) {
      this.$dialog.confirm({
        message: `${this.$t('confirm.saveBeforeNextPage')}`,
        onConfirm: () => {
          this.save().then(() => {
            this.toUiSettingsPage(element_id)
          })
        },
        onCancel: () => {
          this.toUiSettingsPage(element_id)
        }
      })
    },
    toUiSettingsPage(element_id) {
      this.$router.push({
        name: 'edit_ui_element',
        query: {
          dashboard_id: this.$route.params.dashboard_id
        },
        params: {
          ui_element_id: element_id
        }
      })
    },
    toDashboardPage() {
      if (!this.configs.id && !this.$route.params.dashboard_id) {
        this.toDashboards()
        return
      }
      this.$router.push({
        name: 'dashboard',
        params: {
          dashboard_id: this.configs.id || this.$route.params.dashboard_id
        }
      })
    },
    toDashboards() {
      this.$router.push({
        name: 'dashboards'
      })
    },
    save() {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard`
      this.serversLoading = true
      this.configs.name = this.title
      this.configs.elements = this.configs.elements.map(item => ({
        id: item.is_new ? item.id.replace(item.temp, '') : item.id,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        filter_values: item.filter_values
      }))
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          configs: this.configs
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: this.$t('pipeline.saved'),
            type: 'success'
          })
          this.toDashboardPage()
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.serversLoading = false
        })
    },
    create_UUID() {
      var dt = new Date().getTime()
      var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = (dt + Math.random() * 16) % 16 | 0
        dt = Math.floor(dt / 16)
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })
      return uuid
    },
    addElement(element) {
      const temp = `-${this.create_UUID()}`
      this.configs.elements.push({
        id: `${element.id}${temp}`,
        temp: temp,
        x: 0,
        y: 0,
        width: 4,
        height: 3,
        filter_values: [],
        is_new: true
      })
      this.isModalAdd = false
      this.$forceUpdate()
    },
    updateFilter(filters) {
      if (!this.serversLoading) {
        const body = {
          dashboard_id: this.$route.params.dashboard_id,
          element_id: filters.id.replace(filters.temp, ''),
          values: filters.values
        }
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard/update/filters`
        this.serversLoading = true
        return fetch(url, {
          credentials: 'include',
          body: JSON.stringify(body),
          method: 'POST'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    removeBackground() {
      this.configs.background_image = ''
      this.configs.background_color = 'white'
      this.isBackgroundImageModalActive = false
    },
    setBackground() {
      this.configs.background_image = this.tempBackgroundImage
      this.configs.background_color = this.tempBackgroundColor
      this.isBackgroundImageModalActive = false
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    deleteDashboards(dashboard_ids) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboards/delete`
      return fetch(url, {
        credentials: 'include',
        body: JSON.stringify({
          dashboard_ids: dashboard_ids
        }),
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(() => {
          this.$notify({
            message: this.$t('notification.removed', {
              msg: ''
            }),
            type: 'success'
          })
          setTimeout(() => this.toDashboards(), 1000)
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
    },
    openDialogRemove() {
      this.$dialog.confirm({
        message: `${this.$t('confirm.remove', {
          msg: ''
        })}?`,
        onConfirm: () => {
          this.serversLoading = true
          this.deleteDashboards([this.$route.params.dashboard_id]).finally(() => (this.serversLoading = false))
        }
      })
    },
    getUiElements() {
      if (!this.serversLoading) {
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT ||
          window.location.origin}/api/dashboard/elements?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.uiElements = data.ui_elements
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    getDefaultConfig() {
      if (!this.serversLoading) {
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard/edit?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.isDashboardEditAllowed = data.is_editable
            if (!this.isDashboardEditAllowed) {
              this.toDashboardPage()
              return
            }
            this.configs = data.config
            this.uiElements = data.ui_elements
            this.setTitle(this.configs.name)
            this.setDashboardName(this.configs.name)
            this.tempBackgroundImage = this.configs.background_image
            this.tempBackgroundColor = this.configs.background_color
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    exportToXls() {
      if (!this.loadingExport) {
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT ||
          window.location.origin}/api/dashboard/download?${searchParams}`
        this.loadingExport = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            const a = document.createElement('a')
            a.href = data
            a.setAttribute('download', `${this.configs.id}.zip`)
            a.click()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingExport = false
          })
      }
    },
    handleUpload(file) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/upload-file`
      const body = new FormData()
      body.append('file', file)
      body.append('purpose', this.$route.name)
      this.backgroundImageLoading = true
      return fetch(url, {
        credentials: 'include',
        body,
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.tempBackgroundImage = data.name
          // this.configs.background_image = data.name
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.backgroundImageLoading = false
        })
    }
  }
}
</script>

<style lang="scss">
.dashboard-edit {
  .dashboard-layout .dash {
    background-color: var(--page-background-color, #fff);
    background-image: var(--page-background-image);
    background-size: 100%;
  }
  .modal-card {
    h1 {
      font-size: large;
      font-weight: 900;
    }
    .field-label {
      flex-grow: 2;
    }
    .spacer {
      flex-grow: 2;
      margin: 0.5rem;
    }
    .color-selector {
      width: 100%;
    }
    .upload.control {
      width: 100%;
      .upload-draggable {
        width: 100%;
      }
    }
  }
}
.table {
  width: 100%;
}
</style>
